<script setup>
import { ref, onMounted, onUnmounted, computed, defineAsyncComponent } from 'vue'
import { useIO } from '../composables/useIO'
import { useHistory } from '../composables/useHistory'
import { useCommandResolver } from '../composables/useCommandResolver'
import { useAutocomplete } from '../composables/useAutocomplete'
// Lazy-load GameMount so games are only fetched when opened
const GameMount = defineAsyncComponent(() => import('./minigames/GameMount.vue'))
const props = defineProps({ username: { type: String, default: null } })

const { output, scroller, printLine, printBlock, printStructuredLines } = useIO()
const { history, historyIndex, push: pushHistory, prev: historyPrev, next: historyNext } = useHistory()
const input = ref('')
const available = ref([]) // server-provided commands in current directory
const dirs = ref([])
const parent = ref(null)
const cwd = ref('root')
const cdOpen = ref(false)
const cmdOpen = ref(false)
const cdWrap = ref(null)
const cmdWrap = ref(null)
const dirButtons = computed(() => (available.value || []).filter(c => c && c.dir_command === true && c.button === true))
const { resolve: resolveHttpCommand } = useCommandResolver(() => available.value || [])
const { tryAutocomplete: tryAutocompleteFn } = useAutocomplete(() => available.value || [], () => dirs.value || [])
const embedded = ref(null) // { app: 'snake' }
// Current user for prompt/header; can change after login/logout
const currentUser = ref((props.username && props.username.trim()) ? props.username.trim() : null)

// print helpers provided by useIO

function csrfToken() {
  const el = document.querySelector('meta[name="csrf-token"]')
  return el ? el.getAttribute('content') : null
}

const user = computed(() => (currentUser.value && currentUser.value.trim()) ? currentUser.value.trim() : 'deepco')
const hostPrefix = computed(() => `${user.value}@mainframe`)
const promptText = computed(() => `${hostPrefix.value}${cwd.value === 'root' ? '' : '/' + cwd.value}:~$`)
const headerLabel = computed(() => `${hostPrefix.value}${cwd.value === 'root' ? '' : '/' + cwd.value}`)

function doClientAction(action) {
  if (!action) return false
  if (action === 'clear') {
    output.value = []
    return true
  }
  if (action === 'login_prompt') {
    if (currentUser.value) return true
    interact.value = { mode: 'login', step: 'username', data: {} }
    printLine('username:')
    return true
  }
  if (action === 'post_logout') {
    currentUser.value = null
    printLine('logged out.')
    loadDir(cwd.value)
    return true
  }
  if (action === 'mount_snake') {
    embedded.value = { app: 'snake' }
    return true
  }
  if (action === 'help') {
    const lines = []
    if (available.value.length) {
      lines.push('AVAILABLE COMMANDS')
      available.value.forEach(c => {
        const left = (c.usage || c.name || '').padEnd(20, ' ')
        const desc = c.description || ''
        lines.push(`  ${left}# ${desc}`)
      })
    }
    if (dirs.value.length) {
      if (lines.length) lines.push('')
      lines.push('DIRECTORIES')
      dirs.value.forEach(d => lines.push(`  ${String(d)}`))
    }
    printBlock(lines)
    return true
  }
  return false
}

function run(cmd) {
  // No client-only help/clear; provided from backend via commands
  const cdMatch = cmd.match(/^cd\s+(.+)$/)
  if (cdMatch) {
    const arg = cdMatch[1].trim()
    if (arg === '/' || arg === 'root') return loadDir('root', { pushState: true })
    if (arg === '..') return loadDir(parent.value || 'root', { pushState: true })
    if (!dirs.value.includes(arg)) { printLine(`ERR: unknown directory '${arg}'`); return }
    return loadDir(arg, { pushState: true })
  }
  if (cmd === 'quit' || cmd === 'exit') {
    window.location.assign('/')
    return
  }
  // Try a server-provided HTTP command that matches this input, supporting placeholders
  const matched = resolveHttpCommand(cmd)
  if (matched) {
    const started = performance.now()
    const method = (matched.method || 'GET').toUpperCase()
    const headers = { 'Accept': 'application/json' }
    if (method !== 'GET') headers['X-CSRF-Token'] = csrfToken() || ''
    fetch(matched.path, { method, headers })
      .then(async (res) => {
        const took = Math.round(performance.now() - started)
        const contentType = res.headers.get('content-type') || ''
        let bodyText = ''
        if (contentType.includes('application/json')) {
          const data = await res.json()
          // Accept multiple JSON shapes:
          // - Array of lines/segments
          // - { lines: [...] }
          // - string
          // - { segments: [...] } (single line)
          if (Array.isArray(data)) {
            printStructuredLines(data)
            if (matched.meta && matched.meta.show_timing === true) printLine(`(${took}ms)`)
            if (matched.meta && matched.meta.redirect_to) window.location.assign(matched.meta.redirect_to)
            if (matched.meta && matched.meta.client_action) doClientAction(matched.meta.client_action)
            return
          } else if (data && Array.isArray(data.lines)) {
            printStructuredLines(data.lines)
            if (matched.meta && matched.meta.show_timing === true) {
              printLine(`(${took}ms)`) // print timing on a separate line for structured outputs
            }
            if (matched.meta && matched.meta.redirect_to) window.location.assign(matched.meta.redirect_to)
            if (matched.meta && matched.meta.client_action) doClientAction(matched.meta.client_action)
            return
          } else if (typeof data === 'string') {
            bodyText = data
          } else if (data && Array.isArray(data.segments)) {
            printStructuredLines([data])
            if (matched.meta && matched.meta.show_timing === true) printLine(`(${took}ms)`)
            if (matched.meta && matched.meta.redirect_to) window.location.assign(matched.meta.redirect_to)
            if (matched.meta && matched.meta.client_action) doClientAction(matched.meta.client_action)
            return
          }
          bodyText = (data && (data.message ?? JSON.stringify(data))) || ''
        } else {
          bodyText = await res.text()
        }
        if (!res.ok) throw new Error(bodyText || `HTTP ${res.status}`)
        const withTime = matched.meta && (matched.meta.show_timing === true)
        const line = bodyText ? (withTime ? `${bodyText} (${took}ms)` : bodyText) : (withTime ? `OK (${took}ms)` : 'OK')
        printLine(line)
        if (matched.meta && matched.meta.redirect_to) window.location.assign(matched.meta.redirect_to)
        if (matched.meta && matched.meta.client_action) doClientAction(matched.meta.client_action)
      })
      .catch((err) => {
        printLine(`ERR: ${err.message}`)
      })
    return
  }
  // No client-specific command logic beyond built-ins and generic HTTP
  printLine('ERR: command not found. Try `help`.')
}

function submit() {
  const cmd = (input.value || '').trim()
  if (!cmd) return
  // Interactive login flow
  if (interact.value && interact.value.mode === 'login') {
    const step = interact.value.step
    if (step === 'username') {
      interact.value.data.name = cmd
      input.value = ''
      interact.value.step = 'password'
      printLine('password:')
      return
    } else if (step === 'password') {
      interact.value.data.password = cmd
      const payload = JSON.stringify({ name: interact.value.data.name, password: interact.value.data.password })
      input.value = ''
      printLine('attempting login…')
      const headers = { 'Accept': 'application/json', 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() || '' }
      fetch('/terminal/sessions/login', { method: 'POST', headers, body: payload })
        .then(async (res) => {
          if (!res.ok) throw new Error(await res.text())
          const data = await res.json().catch(() => ({}))
          currentUser.value = data.user || interact.value.data.name || currentUser.value
          printLine('login successful.')
          interact.value = null
          // reload commands and keep cwd
          loadDir(cwd.value)
        })
        .catch(err => {
          printLine('please try again')
          interact.value = null
        })
      return
    }
  }
  pushHistory(cmd)
  printLine(`${promptText.value} ${cmd}`)
  run(cmd)
  input.value = ''
}

function onKeydown(e) {
  if (e.key === 'ArrowUp') {
    input.value = historyPrev()
    e.preventDefault()
  } else if (e.key === 'ArrowDown') {
    input.value = historyNext()
    e.preventDefault()
  } else if (e.key === 'Tab') {
    e.preventDefault()
    input.value = tryAutocompleteFn(input.value)
  }
}

// nextTickScroll handled in useIO

// Simple interaction state machine (login prompts)
const interact = ref(null)

function updateUrlForCwd(dir, { push } = { push: false }) {
  try {
    const url = new URL(window.location.href)
    if (dir && dir !== 'root') url.searchParams.set('load', dir)
    else url.searchParams.delete('load')
    const method = push ? 'pushState' : 'replaceState'
    window.history[method]({ cwd: dir }, '', url)
  } catch (_) {}
}

function loadDir(target, opts = {}) {
  const pushState = opts.pushState === true
  // Default: show help after changing directories unless explicitly disabled
  const showHelp = opts.showHelp !== false
  const path = target || 'root'
  printLine(`changing directory to ${path}…`)
  fetch(`/terminal/commands/load?cwd=${encodeURIComponent(path)}`, { headers: { 'Accept': 'application/json' } })
    .then(res => res.json())
    .then(data => {
      cwd.value = data?.cwd || 'root'
      dirs.value = Array.isArray(data?.dirs) ? data.dirs : []
      parent.value = data?.parent || null
      available.value = Array.isArray(data?.commands) ? data.commands : []
      printLine('ready.')
      if (showHelp) {
        printLine(`${promptText.value} help`)
        run('help')
      }
      updateUrlForCwd(cwd.value, { push: pushState })
    })
    .catch((err) => {
      printLine(`ERR: failed to load commands (${err.message || 'unknown'})`)
    })
}

// removed local resolver/autocomplete/print helpers; using composables

onMounted(() => {
  const params = new URLSearchParams(window.location.search)
  const initial = params.get('load') || 'root'
  printLine(`${promptText.value} load ${initial}`)
  printLine('retrieving commands…')
  loadDir(initial, { pushState: false, showHelp: true })
  const onPop = () => {
    const p = new URLSearchParams(window.location.search)
    const dir = p.get('load') || 'root'
    if (dir !== cwd.value) loadDir(dir, { pushState: false })
  }
  window.addEventListener('popstate', onPop)
  onUnmounted(() => window.removeEventListener('popstate', onPop))

  // Close dropdowns on outside click
  const onDocClick = (e) => {
    if (cdOpen.value && cdWrap.value && !cdWrap.value.contains(e.target)) cdOpen.value = false
    if (cmdOpen.value) {
      const insideTrigger = cmdWrap.value && cmdWrap.value.contains(e.target)
      if (!insideTrigger) cmdOpen.value = false
    }
  }
  document.addEventListener('click', onDocClick)
  onUnmounted(() => document.removeEventListener('click', onDocClick))

})

function navTo(dir) {
  loadDir(dir, { pushState: true })
  cdOpen.value = false
}

function execCmd(cmd) {
  const c = (cmd?.usage || cmd?.name || '').trim()
  if (c) { input.value = c; submit() }
  cmdOpen.value = false
}

function toggleCmdMenu() { cmdOpen.value = !cmdOpen.value }
</script>

<template>
  <div class="px-3 pt-3 text-xs sm:text-sm text-terminal-muted border-b border-terminal-border/70 pb-2 flex items-center justify-between">
    <div class="flex items-center gap-3">
      <!-- Header label becomes the cd dropdown trigger -->
      <div ref="cdWrap" class="relative inline-block">
        <div tabindex="0" role="button" class="cursor-pointer select-none hover:underline decoration-dotted" @click="cdOpen = !cdOpen">
          {{ headerLabel }}
        </div>
        <ul v-if="cdOpen" :style="{ maxWidth: 'calc(100vw - 1rem)' }" class="absolute z-20 mt-1 w-[80vw] sm:w-48 max-h-[60vh] overflow-auto p-1 bg-terminal-bg border border-terminal-border/70 rounded-sm">
          <li><a class="block w-full cursor-pointer px-2 py-1 rounded text-terminal-accent hover:text-terminal-accent/90 hover:bg-terminal-border/30" @click.prevent="() => navTo('root')">/</a></li>
          <li><a class="block w-full cursor-pointer px-2 py-1 rounded text-terminal-accent hover:text-terminal-accent/90 hover:bg-terminal-border/30" @click.prevent="() => navTo(parent || 'root')">..</a></li>
          <li v-for="d in dirs" :key="`dir-${d}`"><a class="block w-full cursor-pointer px-2 py-1 rounded text-terminal-accent hover:text-terminal-accent/90 hover:bg-terminal-border/30" @click.prevent="() => navTo(String(d))">{{ d }}</a></li>
        </ul>
      </div>
    </div>
    <a href="/" data-turbo="false" class="text-terminal-accent/90 hover:text-terminal-accent underline decoration-dotted">quit</a>
  </div>
  <div class="px-3 py-3 text-sm md:text-base text-terminal-muted/90 overflow-hidden flex-1">
    <div ref="scroller" class="relative no-scrollbar overflow-y-auto overflow-x-hidden pr-4 md:pr-8 md:mr-[-2rem] overscroll-contain h-full">
      <div v-for="(line, i) in output" :key="i" class="whitespace-pre-wrap break-all md:break-words leading-tight">
        <template v-if="typeof line === 'string'">{{ line }}</template>
        <template v-else>
          <template v-for="(seg, j) in line.segments" :key="j">
            <a v-if="seg.href" :href="seg.href" target="_blank" rel="noopener noreferrer" data-turbo="false" :class="seg.class">{{ seg.text }}</a>
            <span v-else :class="seg.class">{{ seg.text }}</span>
          </template>
        </template>
      </div>
    </div>
  </div>
  <div class="border-t border-terminal-border/70 px-3 py-2">
    <div class="mb-2 flex items-center justify-between gap-3 flex-wrap">
      <div class="flex items-center gap-3 flex-wrap">
        <template v-for="cmd in available" :key="`base-${cmd.name}`">
          <button v-if="cmd.button === true && !cmd.dir_command"
                  type="button"
                  class="rounded-sm border border-terminal-border/70 px-2 py-1 text-xs sm:text-sm text-terminal-accent hover:text-terminal-accent/90"
                  :title="cmd.description || cmd.name"
                  @click="() => execCmd(cmd)">{{ cmd.name }}</button>
        </template>
      </div>
      <!-- Dir-specific commands: simple dropdown on desktop, mobile action-sheet -->
      <div v-if="dirButtons.length > 0" ref="cmdWrap" class="relative inline-block">
        <div tabindex="0" role="button" class="rounded-sm border border-terminal-border/70 px-2 py-1 text-xs sm:text-sm text-terminal-accent hover:text-terminal-accent/90 cursor-pointer select-none" @click="toggleCmdMenu">
          commands
        </div>
        <!-- Desktop/tablet: right-aligned drop-up -->
        <ul v-if="cmdOpen" class="hidden sm:block absolute right-0 bottom-full mb-1 z-40 w-56 max-h-[60vh] overflow-auto p-1 bg-terminal-bg border border-terminal-border/70 rounded-sm">
          <li v-for="cmd in dirButtons" :key="cmd.name">
            <a class="block w-full cursor-pointer px-2 py-1 rounded text-terminal-accent hover:text-terminal-accent/90 hover:bg-terminal-border/30" :title="cmd.description || cmd.name" @click.prevent="() => execCmd(cmd)">{{ cmd.name }}</a>
          </li>
        </ul>
        <!-- Mobile: full-width bottom sheet -->
        <div v-if="cmdOpen" class="sm:hidden fixed inset-0 z-40">
          <div class="absolute inset-0 bg-black/40" @click="cmdOpen = false"></div>
          <div class="absolute bottom-0 left-0 right-0 bg-terminal-bg border-t border-terminal-border/70 rounded-t-md max-h-[70vh] overflow-auto p-2" @click.stop>
            <div class="text-center text-terminal-muted pb-1">commands</div>
            <ul>
              <li v-for="cmd in dirButtons" :key="cmd.name">
                <a class="block w-full cursor-pointer px-3 py-2 rounded text-terminal-accent hover:text-terminal-accent/90 hover:bg-terminal-border/30" :title="cmd.description || cmd.name" @click.prevent="() => execCmd(cmd)">{{ cmd.name }}</a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <form @submit.prevent="submit" class="flex flex-wrap items-center gap-2">
      <span class="text-terminal-muted text-xs sm:text-sm select-none w-full sm:w-auto shrink-0">{{ promptText }}</span>
      <input v-model="input" @keydown="onKeydown" :type="(interact?.mode==='login' && interact?.step==='password') ? 'password' : 'text'" inputmode="text" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false"
              placeholder="type a command"
             class="w-full sm:flex-1 min-w-[200px] bg-transparent outline-none placeholder:text-terminal-muted/50 text-terminal-fg text-base py-2" />
      <button type="submit" class="text-terminal-accent text-xs sm:text-sm underline decoration-dotted hover:text-terminal-accent/90">run</button>
    </form>
  </div>

  <!-- Overlay embedded app covers entire TerminalApp area (relative parent is #terminal-app) -->
  <div v-if="embedded" class="absolute inset-0 z-20 bg-terminal-bg">
    <GameMount class="h-full" :game="embedded.app" @exit="embedded = null" />
  </div>
</template>

<style scoped>
/* Hide native scrollbars without affecting scrollability */
.no-scrollbar {
  -ms-overflow-style: none; /* IE/Edge */
  scrollbar-width: none;    /* Firefox */
}
.no-scrollbar::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;            /* Safari/Chrome */
}
</style>
